import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronRightIcon, ShieldCheckIcon, CheckCircle2Icon, LockIcon, InfoIcon, AlertCircleIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { ConsentWarning } from '../ui/ConsentWarning';
import { Tooltip } from '../ui/Tooltip';
import { AnimatedScreen, staggerContainer, staggerItem } from '../ui/AnimatedScreen';
import { BankTheme } from '../../constants/bankThemes';
export interface Account {
  id: string;
  name: string;
  accountNumber: string;
  balance: number;
  type: string;
  available?: boolean;
  unavailableReason?: string;
}
export interface AccountSelectionScreenProps {
  tppName: string;
  accounts: Account[];
  unavailableAccounts?: Account[];
  expiryDate: string;
  permissions: string[];
  hasAccountSpecificPermissions?: boolean;
  onAuthorize: (selectedAccountIds: string[]) => void;
  onCancel: () => void;
  bankTheme?: BankTheme;
}
export function AccountSelectionScreen({
  tppName,
  accounts,
  unavailableAccounts = [],
  expiryDate,
  permissions,
  hasAccountSpecificPermissions = true,
  onAuthorize,
  onCancel,
  bankTheme
}: AccountSelectionScreenProps) {
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const toggleAccount = (accountId: string) => {
    setSelectedAccounts(prev => prev.includes(accountId) ? prev.filter(id => id !== accountId) : [...prev, accountId]);
  };
  const selectAll = () => {
    setSelectedAccounts(accounts.map(acc => acc.id));
  };
  const deselectAll = () => {
    setSelectedAccounts([]);
  };
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && selectedAccounts.length > 0) {
        onAuthorize(selectedAccounts);
      }
      if (e.key === 'Escape') {
        onCancel();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedAccounts, onAuthorize, onCancel]);
  // Use bank theme colors if available
  const primaryColor = bankTheme?.colors.primary || '#4DB8C4';
  const primaryGradient = bankTheme?.gradients.button || 'linear-gradient(135deg, #4DB8C4 0%, #2a5a8f 100%)';
  const accentColor = bankTheme?.colors.accent || '#E6F4F7';
  const borderRadius = bankTheme?.borderRadius || '12px';
  return <AnimatedScreen data-id="element-109">
      <div className="space-y-6 p-6" data-id="element-110">
        <div data-id="element-111">
          <h2 className="text-xl font-bold text-gray-900 mb-2 leading-tight" data-id="element-112">
            Select account(s) to share information with {tppName}
          </h2>
        </div>

        {/* CDCS-7.5, 7.6: Non-account-specific permissions notice */}
        {!hasAccountSpecificPermissions && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="p-4 rounded-xl" style={{
        backgroundColor: accentColor,
        borderRadius
      }} data-id="element-113">
            <div className="flex items-start gap-3" data-id="element-114">
              <InfoIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
            color: primaryColor
          }} data-id="element-115" />
              <div className="text-sm" style={{
            color: bankTheme?.colors.text || '#1A1A1A'
          }} data-id="element-116">
                <div className="font-semibold mb-1" data-id="element-117">
                  No Account Selection Required
                </div>
                <div className="text-xs opacity-80" data-id="element-118">
                  This consent only requests your personal information (Contact
                  and Party Details). No account-specific data will be shared.
                </div>
              </div>
            </div>
          </motion.div>}

        {/* Quick Actions - Bank branded */}
        {hasAccountSpecificPermissions && accounts.length > 1 && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="flex gap-2" data-id="element-119">
            <button onClick={selectAll} className="flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors" style={{
          backgroundColor: accentColor,
          color: primaryColor,
          borderRadius
        }} data-id="element-120">
              Select All
            </button>
            <button onClick={deselectAll} className="flex-1 px-3 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 text-sm font-medium transition-colors" style={{
          borderRadius
        }} data-id="element-121">
              Deselect All
            </button>
          </motion.div>}

        {/* Available Accounts with staggered animation - Bank branded */}
        {hasAccountSpecificPermissions && <motion.div variants={staggerContainer} initial="hidden" animate="show" className="space-y-2.5" data-id="element-122">
            {accounts.map(account => {
          const isSelected = selectedAccounts.includes(account.id);
          return <motion.button key={account.id} variants={staggerItem} onClick={() => toggleAccount(account.id)} whileHover={{
            scale: 1.01
          }} whileTap={{
            scale: 0.99
          }} className="w-full p-4 border-2 transition-all duration-200 text-left relative" style={{
            borderColor: isSelected ? primaryColor : '#E5E7EB',
            backgroundColor: isSelected ? accentColor : 'white',
            borderRadius,
            boxShadow: isSelected ? bankTheme?.shadowStyle : 'none'
          }} data-id="element-123">
                  <div className="flex items-start justify-between" data-id="element-124">
                    <div className="flex-1 pr-8" data-id="element-125">
                      <div className="font-bold text-gray-900 mb-1" data-id="element-126">
                        {account.name}
                      </div>
                      <div className="text-sm text-gray-600 font-mono" data-id="element-127">
                        {account.accountNumber.replace(/(\d{4})(?=\d)/g, '$1 ')}
                      </div>
                      <div className="text-xs text-gray-500 mt-1 font-medium" data-id="element-128">
                        {account.type}
                      </div>
                    </div>
                    <div className="text-right" data-id="element-129">
                      <div className="text-xs text-gray-500 mb-1" data-id="element-130">Balance</div>
                      <div className="font-bold text-gray-900 text-base" data-id="element-131">
                        AED {account.balance.toLocaleString()}
                      </div>
                    </div>
                  </div>
                  {isSelected && <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} className="absolute top-4 right-4 w-6 h-6 rounded-full flex items-center justify-center shadow-md" style={{
              backgroundColor: primaryColor
            }} data-id="element-132">
                      <CheckCircle2Icon className="w-4 h-4 text-white" strokeWidth={3} data-id="element-133" />
                    </motion.div>}
                </motion.button>;
        })}
          </motion.div>}

        {/* CDCS-7.7: Unavailable Accounts */}
        {hasAccountSpecificPermissions && unavailableAccounts.length > 0 && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="space-y-2.5" data-id="element-134">
            <div className="text-sm font-semibold text-gray-500 uppercase tracking-wide" data-id="element-135">
              Unavailable Accounts
            </div>
            {unavailableAccounts.map(account => <div key={account.id} className="p-4 border-2 border-gray-200 bg-gray-50 opacity-60" style={{
          borderRadius
        }} data-id="element-136">
                <div className="flex items-center justify-between" data-id="element-137">
                  <div className="flex-1" data-id="element-138">
                    <div className="font-bold text-gray-500 mb-1" data-id="element-139">
                      {account.name}
                    </div>
                    <div className="text-sm text-gray-500 font-mono mb-2" data-id="element-140">
                      {account.accountNumber.replace(/(\d{4})(?=\d)/g, '$1 ')}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-red-600" data-id="element-141">
                      <AlertCircleIcon className="w-4 h-4" data-id="element-142" />
                      <span className="font-medium" data-id="element-143">
                        Unavailable:{' '}
                        {account.unavailableReason || 'Account locked or restricted'}
                      </span>
                    </div>
                  </div>
                  <LockIcon className="w-5 h-5 text-gray-400" data-id="element-144" />
                </div>
              </div>)}
          </motion.div>}

        {/* Permissions List with improved icons matching the design */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="space-y-3" data-id="element-145">
          <h3 className="text-base font-bold text-gray-900 mb-3" data-id="element-146">
            Review the information you will be sharing
          </h3>
          {permissions.map((permission, index) => {
          // Map permissions to appropriate icons
          const getPermissionIcon = (perm: string) => {
            const lowerPerm = perm.toLowerCase();
            if (lowerPerm.includes('account details')) {
              return <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-147">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" data-id="element-148" />
                    <circle cx="12" cy="7" r="4" data-id="element-149" />
                  </svg>;
            }
            if (lowerPerm.includes('regular payments') || lowerPerm.includes('payment')) {
              return <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-150">
                    <circle cx="12" cy="12" r="10" data-id="element-151" />
                    <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" data-id="element-152" />
                    <path d="M12 18V6" data-id="element-153" />
                  </svg>;
            }
            if (lowerPerm.includes('transactions')) {
              return <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-154">
                    <rect x="2" y="7" width="20" height="14" rx="2" data-id="element-155" />
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" data-id="element-156" />
                  </svg>;
            }
            if (lowerPerm.includes('statements')) {
              return <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-157">
                    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" data-id="element-158" />
                    <rect x="8" y="2" width="8" height="4" rx="1" data-id="element-159" />
                    <path d="M12 11h4" data-id="element-160" />
                    <path d="M12 16h4" data-id="element-161" />
                    <path d="M8 11h.01" data-id="element-162" />
                    <path d="M8 16h.01" data-id="element-163" />
                  </svg>;
            }
            if (lowerPerm.includes('product') || lowerPerm.includes('features') || lowerPerm.includes('benefits')) {
              return <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-164">
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" data-id="element-165" />
                    <polyline points="7.5 4.21 12 6.81 16.5 4.21" data-id="element-166" />
                    <polyline points="7.5 19.79 7.5 14.6 3 12" data-id="element-167" />
                    <polyline points="21 12 16.5 14.6 16.5 19.79" data-id="element-168" />
                    <polyline points="3.27 6.96 12 12.01 20.73 6.96" data-id="element-169" />
                    <line x1="12" y1="22.08" x2="12" y2="12" data-id="element-170" />
                  </svg>;
            }
            if (lowerPerm.includes('contact') || lowerPerm.includes('party')) {
              return <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-171">
                    <rect x="3" y="3" width="7" height="7" data-id="element-172" />
                    <rect x="14" y="3" width="7" height="7" data-id="element-173" />
                    <rect x="14" y="14" width="7" height="7" data-id="element-174" />
                    <rect x="3" y="14" width="7" height="7" data-id="element-175" />
                  </svg>;
            }
            // Default icon
            return <ShieldCheckIcon className="w-5 h-5" data-id="element-176" />;
          };
          return <div key={index} className="w-full flex items-center justify-between p-4 bg-white border border-gray-200 hover:border-gray-300 transition-colors" style={{
            borderRadius
          }} data-id="element-177">
                <div className="flex items-center space-x-3" data-id="element-178">
                  <div className="w-9 h-9 flex items-center justify-center flex-shrink-0" style={{
                color: '#1A1A1A'
              }} data-id="element-179">
                    {getPermissionIcon(permission)}
                  </div>
                  <span className="text-sm font-medium text-gray-900" data-id="element-180">
                    {permission}
                  </span>
                </div>
              </div>;
        })}
        </motion.div>

        <ConsentWarning expiryDate={expiryDate} variant="share" durationMonths={6} data-id="element-181" />

        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="pt-2" data-id="element-182">
          {/* Stacked buttons - Cancel on top, Authorize below */}
          <div className="space-y-3" data-id="element-183">
            {/* Cancel Button with gradient border */}
            <button onClick={onCancel} className="relative w-full px-6 py-4 bg-white font-semibold transition-all hover:bg-gray-50 overflow-hidden" style={{
            borderRadius: '50px'
          }} data-id="element-184">
              {/* Gradient border effect */}
              <div className="absolute inset-0 rounded-full p-[2px]" style={{
              background: 'linear-gradient(135deg, #00BCD4 0%, #1976D2 100%)',
              WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
              WebkitMaskComposite: 'xor',
              maskComposite: 'exclude'
            }} data-id="element-185" />
              <span className="relative z-10" style={{
              background: 'linear-gradient(135deg, #00BCD4 0%, #1976D2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }} data-id="element-186">
                Cancel
              </span>
            </button>

            {/* Authorize Button with gradient background and ALTAREQ logo */}
            <button onClick={() => onAuthorize(selectedAccounts)} disabled={hasAccountSpecificPermissions && selectedAccounts.length === 0} className="w-full px-6 py-4 text-white font-semibold transition-all hover:opacity-90 disabled:opacity-50" style={{
            background: 'linear-gradient(90deg, #00BCD4 0%, #1976D2 50%, #000000 100%)',
            borderRadius: '50px'
          }} data-id="element-187">
              <div className="flex items-center justify-center gap-3" data-id="element-188">
                <img src="https://cdn.magicpatterns.com/uploads/8Zyr2eTymP8hQsszbaoPhJ/White_Mark.svg" alt="ALTAREQ" className="h-6 w-6 object-contain" data-id="element-189" />
                <span data-id="element-190">Authorize using Al Tareq</span>
              </div>
            </button>
          </div>

          {/* Keyboard shortcuts hint */}
          <div className="text-center text-xs text-gray-500 mt-4" data-id="element-191">
            Press{' '}
            <kbd className="px-2 py-1 bg-gray-100 rounded border border-gray-300 font-mono" data-id="element-192">
              Enter
            </kbd>{' '}
            to continue or{' '}
            <kbd className="px-2 py-1 bg-gray-100 rounded border border-gray-300 font-mono" data-id="element-193">
              Esc
            </kbd>{' '}
            to cancel
          </div>
        </motion.div>
      </div>
    </AnimatedScreen>;
}